import { createSlice } from "@reduxjs/toolkit";
import { normalizeChatMessage } from "../../utils/storedFile";

const initialState = {
  list: [],
  files: [],
  selected: {},
  currentRoomId: "",
  connection: null,
  isLoaded: false,
};

const normalizeRoomMessages = (room) => {
  if (!room || typeof room !== "object") return room;
  if (!Array.isArray(room.messages)) return room;
  return {
    ...room,
    messages: room.messages.map((msg) => normalizeChatMessage(msg)),
  };
};

const messageRoomsSlice = createSlice({
  name: "messageRoom",
  initialState,
  reducers: {
    clearMessageRooms: (state) => {
      state.list = [];
      state.files = [];
      state.selected = {};
      state.currentRoomId = "";
      state.connection = null;
      state.isLoaded = false;
    },
    setCurrentRoomId: (state, { payload }) => {
      if (state.currentRoomId !== payload)
        state.currentRoomId = payload;
    },
    setConnection: (state, { payload }) => {
      state.connection = payload;
    },
    setMessageFiles: (state, { payload }) => {
      state.files = payload
    },
    addMessageFiles: (state, { payload }) => {
      state.files = [...state.files, payload]
    },
    setMessageRooms: (state, { payload }) => {
      state.list = (payload || []).map(normalizeRoomMessages);
      state.isLoaded = true;
    },
    removeMessageRoomFromList: (state, { payload }) => {
      state.list = state.list.filter((item) => item.id !== payload);
    },
    /** Listede sırayı koru: varsa aynı indekste birleştir, yoksa sona ekle (sona taşıma yapma). */
    addMessageRoomToList: (state, { payload }) => {
      const normalized = normalizeRoomMessages(payload);
      const idx = state.list.findIndex((item) => item.id === normalized.id);
      if (idx !== -1) {
        state.list[idx] = { ...state.list[idx], ...normalized };
      } else {
        state.list.push(normalized);
      }
    },
    updateMessageRoomListMessages: (state, { payload }) => {
      const roomIndex = state.list.findIndex(d => d.id === payload.id);
      if (roomIndex !== -1) {
        const room = state.list[roomIndex];
        if (!room.messages) {
          room.messages = [];
        }
        const incoming = (payload.messages || []).map((msg) => normalizeChatMessage(msg));
        room.messages = [...incoming, ...room.messages];
      }
    },
    setSelectedMessageRoom: (state, { payload }) => {
      state.selected = normalizeRoomMessages(payload);
    },
    updateSelectedMessageRoomMessages: (state, { payload }) => {
      if (state.selected.id === payload.id) {
        const incoming = (payload.messages || []).map((msg) => normalizeChatMessage(msg));
        state.selected.messages = [...incoming, ...(state.selected.messages || [])];
      }
    },
    clearSelectedMessageRoom: (state) => {
      state.selected = {};
    },
    updateMessageRoom: (state, { payload }) => {
      state.list = state.list.map((item) =>
        item.id === payload.id ? { ...item, ...payload } : item
      );
      // Also update selected room if it's the same room
      if (state.selected && state.selected.id === payload.id) {
        state.selected = { ...state.selected, ...payload };
      }
    },
    addMessageToRoom: (state, { payload }) =>
    {
      const normalized = normalizeChatMessage(payload || {});
      const incoming = {
        ...normalized,
        attachments: Array.isArray(normalized?.attachments)
          ? normalized.attachments
          : [],
      };
      // Sadece ilgili odayı bul ve direkt güncelle (Immer mutation - diğer odaların referansı korunur)
      const roomIndex = state.list.findIndex(item => item.id === incoming.roomId);
      if (roomIndex !== -1) {
        const room = state.list[roomIndex];
        if (!room.messages) {
          room.messages = [];
        }
        const existingIndex = room.messages.findIndex(msg => msg.id === incoming.id);
        if (existingIndex === -1) {
          room.messages.push(incoming);
        } else {
          const existing = room.messages[existingIndex];
          const existingAtt = existing.attachments?.length || 0;
          const incomingAtt = incoming.attachments?.length || 0;
          // Hub eki eksik gönderdiyse mevcut ekleri koru; aksi halde gelenle güncelle
          room.messages[existingIndex] = {
            ...existing,
            ...incoming,
            attachments:
              incomingAtt > 0 || existingAtt === 0
                ? incoming.attachments
                : existing.attachments,
          };
        }
      }

      // Sadece seçili oda bu oda ise güncelle
      if (state.selected && state.selected.id === incoming.roomId) {
        if (!state.selected.messages) {
          state.selected.messages = [];
        }
        const existingIndex = state.selected.messages.findIndex(msg => msg.id === incoming.id);
        if (existingIndex === -1) {
          state.selected.messages.push(incoming);
        } else {
          const existing = state.selected.messages[existingIndex];
          const existingAtt = existing.attachments?.length || 0;
          const incomingAtt = incoming.attachments?.length || 0;
          state.selected.messages[existingIndex] = {
            ...existing,
            ...incoming,
            attachments:
              incomingAtt > 0 || existingAtt === 0
                ? incoming.attachments
                : existing.attachments,
          };
        }
      }
    },
    patchMessageAttachments: (state, { payload }) => {
      const { roomId, messageId, attachments } = payload || {};
      if (!roomId || !messageId || !Array.isArray(attachments)) return;
      const apply = (room) => {
        if (!room?.messages) return;
        const idx = room.messages.findIndex((m) => m.id === messageId);
        if (idx === -1) return;
        const current = room.messages[idx];
        if ((current.attachments?.length || 0) > 0) return;
        room.messages[idx] = { ...current, attachments };
      };
      const listIdx = state.list.findIndex((item) => item.id === roomId);
      if (listIdx !== -1) apply(state.list[listIdx]);
      if (state.selected?.id === roomId) apply(state.selected);
    },
  },
});

export const {
  setMessageFiles,
  addMessageFiles,
  setConnection,
  setMessageRooms,
  setSelectedMessageRoom,
  clearSelectedMessageRoom,
  addMessageRoomToList,
  removeMessageRoomFromList,
  updateMessageRoom,
  addMessageToRoom,
  patchMessageAttachments,
  updateSelectedMessageRoomMessages,
  updateMessageRoomListMessages,
  clearMessageRooms,
  setCurrentRoomId
} = messageRoomsSlice.actions;
export default messageRoomsSlice.reducer;